import React, { useMemo } from 'react';
import { getChampionIconUrl } from '../../lib/champions';
import { computeChampionStats, detectStrength, detectWeakness, detectSuggestion } from '../../lib/analytics';
import { MONTH_MS } from '../../constants';

/**
 * APEX v8.0 — Dashboard Bottom Panel
 *
 * Affiché sous la courbe LP dans DashboardCenterPanel.
 * Deux blocs côte à côte :
 *   - Top 5 Champions WR (30j) — version compacte sans scroll
 *   - Insights du jour (1-3 cards)
 *
 * Props:
 *   games        : games filtrées
 *   version      : DDragon version string
 *   userSettings : pour les insights (champion_pool)
 *   onSuggestionClick : callback (action) => void
 *   onSeeAll     : callback "Voir tous les champions"
 */



function getWrColor(wr) {
  if (wr >= 60) return 'text-cyan-400';
  if (wr >= 50) return 'text-emerald-400';
  if (wr >= 40) return 'text-amber-400';
  return 'text-rose-400';
}

function getBarColor(wr) {
  if (wr >= 60) return 'bg-cyan-400';
  if (wr >= 50) return 'bg-emerald-400';
  if (wr >= 40) return 'bg-amber-400';
  return 'bg-rose-400';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ChampionRow({ champ, version }) {
  const barColor = getBarColor(champ.wr);
  const wrColor  = getWrColor(champ.wr);

  return (
    <div className="flex items-center gap-2.5 py-1.5">
      {/* Icon */}
      <img
        src={getChampionIconUrl(champ.name, version)}
        alt={champ.name}
        className="w-8 h-8 object-cover border border-zinc-800 shrink-0"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      {/* Name + bar */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-1">
          <span className="text-[11px] font-bold text-white truncate">{champ.name}</span>
          <span className="text-[10px] text-zinc-500 font-mono shrink-0">{champ.total}g</span>
        </div>
        <div className="mt-1 h-[3px] bg-zinc-800 overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${champ.wr}%` }} />
        </div>
      </div>
      {/* WR */}
      <div className={`font-mono font-black text-sm ${wrColor} shrink-0 w-9 text-right`}>
        {champ.wr}%
      </div>
    </div>
  );
}

function InsightMiniCard({ insight, onClick }) {
  const colorMap = {
    cyan:   { border: 'border-cyan-400/30',   accent: 'bg-cyan-400',   text: 'text-cyan-400' },
    amber:  { border: 'border-amber-400/30',  accent: 'bg-amber-400',  text: 'text-amber-400' },
    violet: { border: 'border-violet-400/30', accent: 'bg-violet-400', text: 'text-violet-400' },
  };
  const c = colorMap[insight.barColor] || colorMap.cyan;
  const clickable = !!insight.action;

  return (
    <div
      onClick={clickable ? () => onClick?.(insight.action) : undefined}
      className={`border ${c.border} bg-zinc-950 relative ${clickable ? 'cursor-pointer hover:border-opacity-100 transition' : ''}`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${c.accent}`} />
      <div className="pl-3 pr-3 py-2.5">
        <div className={`text-[9px] tracking-[0.25em] uppercase font-semibold ${c.text} mb-0.5`}>
          {insight.eyebrow}
        </div>
        <div className="text-[11px] font-bold text-white leading-snug">
          {insight.title}
        </div>
        <div className="text-[10px] text-zinc-500 font-mono leading-relaxed mt-0.5">
          {insight.detail}
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function DashboardBottomPanel({
  games = [],
  version,
  userSettings,
  onSuggestionClick,
  onSeeAll,
}) {
  const safeGames = Array.isArray(games) ? games : [];

  // Champions top 5
  const champStats = useMemo(() => computeChampionStats(safeGames, 30), [safeGames]);
  const top5 = champStats.slice(0, 5);

  // Insights
  const insights = useMemo(() => {
    const cutoff = Date.now() - MONTH_MS;
    const recent = safeGames.filter(g => g && new Date(g.timestamp) > cutoff);
    const list = [];
    const s = detectStrength(recent);   if (s) list.push(s);
    const w = detectWeakness(recent);   if (w) list.push(w);
    const sg = detectSuggestion(safeGames, userSettings); if (sg) list.push(sg);
    return list;
  }, [safeGames, userSettings]);

  const hasChamps   = top5.length > 0;
  const hasInsights = insights.length > 0;

  if (!hasChamps && !hasInsights) return null;

  return (
    <div className="border border-zinc-800 bg-zinc-950 p-5 mt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">

        {/* ── Bloc Champions ── */}
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-semibold mb-2">
            Champions · 30J
          </div>

          {hasChamps ? (
            <>
              <div className="divide-y divide-zinc-800/60">
                {top5.map(champ => (
                  <ChampionRow key={champ.name} champ={champ} version={version} />
                ))}
              </div>
              {champStats.length > 5 && (
                <button
                  onClick={onSeeAll}
                  className="mt-2 w-full text-[10px] font-bold tracking-[0.2em] text-cyan-400 hover:text-cyan-300 transition py-1"
                >
                  VOIR TOUS LES CHAMPIONS →
                </button>
              )}
            </>
          ) : (
            <div className="text-xs text-zinc-500">Pas de games sur 30j.</div>
          )}
        </div>

        {/* ── Bloc Insights ── */}
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-semibold mb-2">
            Insights du jour
          </div>

          {hasInsights ? (
            <div className="space-y-1.5">
              {insights.map((insight, idx) => (
                <InsightMiniCard
                  key={idx}
                  insight={insight}
                  onClick={onSuggestionClick}
                />
              ))}
            </div>
          ) : (
            <div className="text-xs text-zinc-500">Pas assez de données.</div>
          )}
        </div>

      </div>
    </div>
  );
}
